import { useEffect, useRef, useState } from 'react';
import DiceBox from '@3d-dice/dice-box-threejs';

const DICE_TYPES = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20'] as const;
const DICE_VALUES: Record<typeof DICE_TYPES[number], number> = {
  d4: 4,
  d6: 6,
  d8: 8,
  d10: 10,
  d12: 12,
  d20: 20,
};

/**
 * Utility component to capture dice renders as PNGs
 * Access via: http://localhost:3000/#capture
 */
export function DiceCapture() {
  const containerRef = useRef<HTMLDivElement>(null);
  const diceBoxRef = useRef<DiceBox | null>(null);
  const [status, setStatus] = useState('Initializing...');
  const [currentDie, setCurrentDie] = useState<string>('-');
  const [isReady, setIsReady] = useState(false);
  const [dieIndex, setDieIndex] = useState(0);

  useEffect(() => {
    if (!containerRef.current || diceBoxRef.current) return;

    const init = async () => {
      try {
        // Note: some physics options are valid at runtime but not in TypeScript definitions
        const diceBox = new DiceBox('#dice-capture-container', {
          assetPath: '/dice/',
          framerate: 1 / 60,
          sounds: false,
          volume: 0,
          color_spotlight: 0xd4af37,
          shadows: true,
          theme_surface: 'green-felt',
          theme_colorset: 'bronze',
          theme_texture: 'bronze03',
          theme_material: 'metal',
          gravity_multiplier: 800,
          light_intensity: 1.2,
          baseScale: 200,
          strength: 0.1,         // Very low throw strength - dice stay centered
          startingHeight: 5,     // Lower drop height
          spinForce: 2,          // Less spin
          throwForce: 1,         // Minimal throw
        } as any);

        await diceBox.initialize();
        diceBoxRef.current = diceBox;
        setIsReady(true);
        setStatus('Ready! Click "Roll Next Die" to start.');
      } catch (err) {
        console.error('DiceBox init failed:', err);
        setStatus(`Error: ${err}`);
      }
    };

    init();
  }, []);

  const rollNext = async () => {
    if (!diceBoxRef.current || dieIndex >= DICE_TYPES.length) return;

    const type = DICE_TYPES[dieIndex];
    setCurrentDie(type.toUpperCase());
    setStatus(`Rolling ${type.toUpperCase()}...`);

    try {
      // Force the die to show its max value (d20 shows 20, etc.)
      const value = DICE_VALUES[type];
      await diceBoxRef.current.roll(`1${type}@${value}`);
      setStatus(`${type.toUpperCase()} showing ${value}! Right-click canvas → "Save Image As..." → ${type}.png`);
      setDieIndex(dieIndex + 1);
    } catch (err) {
      console.error('Roll failed:', err);
      setStatus(`Roll failed: ${err}`);
    }
  };

  const reroll = async () => {
    if (!diceBoxRef.current || dieIndex === 0) return;
    const type = DICE_TYPES[dieIndex - 1];
    const value = DICE_VALUES[type];
    setStatus(`Re-rolling ${type.toUpperCase()}...`);
    await diceBoxRef.current.roll(`1${type}@${value}`);
    setStatus(`${type.toUpperCase()} showing ${value}! Right-click to save.`);
  };

  return (
    <div style={{ background: '#1a1a1a', color: 'white', padding: 20, minHeight: '100vh' }}>
      <h1 style={{ color: '#d4af37' }}>Dice Icon Capture Utility</h1>

      <div style={{ background: '#333', padding: 16, borderRadius: 8, marginBottom: 20 }}>
        <p><strong>Instructions:</strong></p>
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          <li>Click "Roll Next Die" to render each die type</li>
          <li>Right-click the canvas → "Save Image As..."</li>
          <li>Save to <code>apps/web/public/dice/icons/d4.png</code>, etc.</li>
        </ol>
      </div>

      <div style={{ marginBottom: 16 }}>
        <button
          onClick={rollNext}
          disabled={!isReady || dieIndex >= DICE_TYPES.length}
          style={{
            background: '#d4af37',
            color: '#1a1a1a',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 4,
            cursor: isReady ? 'pointer' : 'not-allowed',
            fontWeight: 'bold',
            marginRight: 8,
            opacity: isReady && dieIndex < DICE_TYPES.length ? 1 : 0.5,
          }}
        >
          {dieIndex >= DICE_TYPES.length ? 'All Done!' : `Roll Next Die (${DICE_TYPES[dieIndex]?.toUpperCase()})`}
        </button>
        <button
          onClick={reroll}
          disabled={!isReady || dieIndex === 0}
          style={{
            background: '#555',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: 4,
            cursor: isReady && dieIndex > 0 ? 'pointer' : 'not-allowed',
            fontWeight: 'bold',
            opacity: isReady && dieIndex > 0 ? 1 : 0.5,
          }}
        >
          Re-roll Current
        </button>
      </div>

      <div style={{ background: '#222', padding: 10, borderRadius: 4, marginBottom: 10 }}>
        {status}
      </div>

      <div style={{ fontSize: 24, color: '#d4af37', marginBottom: 10 }}>
        Current: {currentDie}
      </div>

      <div
        id="dice-capture-container"
        ref={containerRef}
        style={{
          width: 400,
          height: 400,
          background: 'radial-gradient(ellipse at center, #1e5c3a 0%, #0f2d1a 100%)',
          borderRadius: 12,
          border: '4px solid #2d1f15',
        }}
      />

      <p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>
        Progress: {dieIndex}/{DICE_TYPES.length} dice captured
      </p>
    </div>
  );
}
